import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaBuilding, FaPalette, FaFileAlt, FaCog, FaEye,
  FaSave, FaTimesCircle, FaCheckCircle, FaExclamationCircle,
  FaUpload, FaFont, FaClock, FaGlobe, FaDollarSign,
  FaImage, FaAlignLeft, FaUndo, FaLock, FaEnvelope, FaShieldAlt,
  FaDatabase, FaPlus, FaEdit, FaTrash, FaTimes, FaCheck,
  FaGraduationCap, FaChartBar, FaListUl, FaUserTag, FaLayerGroup,
  FaChevronDown, FaChevronUp, FaInfoCircle, FaToggleOn,
  FaBirthdayCake, FaBaseballBall,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import SixersLogo from "./Sixers_Logo.png";

import API_BASE from "./apiConfig";
import { useTheme, getT } from "./ThemeContext";

const BASE = API_BASE;

const BRAND = {
  blur:    "blur(18px)",
  yellow:  Colors.logoYellow || "#f7ce0b",
  blue:    Colors.primaryBlue || "#034e7b",
  font:    "'Segoe UI', system-ui, -apple-system, sans-serif",
  success: "#10b981",
  danger:  "#ef4444",
};

function makeS(T) {
  return {
    lbl:  { display:"block", marginBottom:6, fontSize:11, fontWeight:800, letterSpacing:"0.07em", textTransform:"uppercase", color:T.textLabel, fontFamily:BRAND.font },
    wrap: { display:"flex", alignItems:"center", border:`1.5px solid ${T.inputBdr}`, borderRadius:10, padding:"10px 14px", background:T.inputBg, backdropFilter:BRAND.blur, WebkitBackdropFilter:BRAND.blur, width:"100%", boxSizing:"border-box" },
    inp:  { border:"none", outline:"none", width:"100%", background:"transparent", fontSize:14, color:T.inputText, fontFamily:BRAND.font },
    card: { background:T.glass, backdropFilter:BRAND.blur, WebkitBackdropFilter:BRAND.blur, border:T.glassBdr, borderRadius:18, boxShadow:T.glassSh, padding:"28px 32px", boxSizing:"border-box" },
    yBtn: (sm) => ({ padding:sm?"8px 14px":"10px 20px", border:"none", borderRadius:10, fontSize:sm?12:13, fontWeight:700, cursor:"pointer", WebkitTapHighlightColor:"transparent", color:"#02304f", display:"flex", alignItems:"center", justifyContent:"center", gap:6, background:`linear-gradient(135deg,${T.yellow},#fff3a0)`, boxShadow:"0 4px 14px rgba(255,200,0,0.28)", fontFamily:BRAND.font, whiteSpace:"nowrap" }),
    bBtn: (sm) => ({ padding:sm?"8px 14px":"10px 20px", border:`1px solid ${T.inputBdr}`, borderRadius:10, fontSize:sm?12:13, fontWeight:700, cursor:"pointer", WebkitTapHighlightColor:"transparent", color:T.inputText, display:"flex", alignItems:"center", justifyContent:"center", gap:6, background:T.glass, backdropFilter:BRAND.blur, WebkitBackdropFilter:BRAND.blur, boxShadow:"0 4px 14px rgba(0,0,0,0.20)", fontFamily:BRAND.font, whiteSpace:"nowrap" }),
    gBtn: { padding:"6px 12px", border:`1.5px solid ${T.inputBdr}`, borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", WebkitTapHighlightColor:"transparent", color:T.textLabel, display:"flex", alignItems:"center", gap:5, background:T.glass, fontFamily:BRAND.font },
    dBtn: { padding:"6px 12px", border:"1.5px solid rgba(239,68,68,0.25)", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", WebkitTapHighlightColor:"transparent", color:"#dc2626", display:"flex", alignItems:"center", gap:5, background:"rgba(239,68,68,0.06)", fontFamily:BRAND.font },
    sBtn: { padding:"6px 12px", border:"1.5px solid rgba(16,185,129,0.25)", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", WebkitTapHighlightColor:"transparent", color:"#059669", display:"flex", alignItems:"center", gap:5, background:"rgba(16,185,129,0.06)", fontFamily:BRAND.font },
  };
}

const mkToast = (ok) => ({
  display:"flex", alignItems:"center", gap:10, padding:"12px 16px", borderRadius:10, marginBottom:16,
  fontSize:13, fontWeight:600, fontFamily:BRAND.font, cursor:"pointer", WebkitTapHighlightColor:"transparent",
  background: ok ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
  color:      ok ? "#34d399" : "#f87171",
  border:     ok ? "1px solid rgba(52,211,153,0.30)" : "1px solid rgba(248,113,113,0.30)",
  boxShadow:  "0 2px 8px rgba(0,0,0,0.15)",
});

const mkTabBtn = (active, T) => ({
  padding:"9px 16px", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer", WebkitTapHighlightColor:"transparent",
  display:"flex", alignItems:"center", gap:7, fontFamily:BRAND.font, whiteSpace:"nowrap",
  color:      active ? "#02304f" : T.textLabel,
  background: active ? `linear-gradient(135deg,${T.yellow},#fff3a0)` : T.glass,
  border:     active ? "1.5px solid rgba(255,200,0,0.45)" : T.glassBdr,
  boxShadow:  active ? "0 4px 14px rgba(255,200,0,0.28)" : T.glassSh,
  backdropFilter: BRAND.blur, WebkitBackdropFilter: BRAND.blur,
});

// ── Micro-components ──────────────────────────────────────────────────────────
function Hint({ c }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return <p style={{ fontSize:11, color:T.textMuted, margin:"4px 0 0", fontFamily:BRAND.font }}>{c}</p>;
}

function SectionDiv({ children }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, margin:"20px 0 14px", gridColumn:"1/-1" }}>
      <div style={{ height:2, width:16, background:T.yellow, borderRadius:2 }} />
      <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.10em", textTransform:"uppercase", color:T.textLabel, fontFamily:BRAND.font }}>{children}</span>
      <div style={{ flex:1, height:1, background:T.divider }} />
    </div>
  );
}

function FldText({ label, icon, name, type="text", value, onChange, placeholder, hint, maxLength, disabled }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  return (
    <div>
      <label style={S.lbl}>{label}</label>
      <div style={S.wrap}>
        {icon && React.cloneElement(icon, { style:{ marginRight:10, color:T.textMuted, flexShrink:0 } })}
        <input type={type} name={name} value={value} onChange={onChange}
          placeholder={placeholder} maxLength={maxLength} disabled={disabled}
          style={{ ...S.inp, ...(disabled ? { opacity:0.45, cursor:"not-allowed" } : {}) }} />
      </div>
      {hint && <Hint c={hint}/>}
    </div>
  );
}

function FldSelect({ label, icon, name, value, onChange, options, hint }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  return (
    <div>
      <label style={S.lbl}>{label}</label>
      <div style={S.wrap}>
        {icon && React.cloneElement(icon, { style:{ marginRight:10, color:T.textMuted, flexShrink:0 } })}
        <select name={name} value={value} onChange={onChange} style={{ ...S.inp, cursor:"pointer", WebkitTapHighlightColor:"transparent", colorScheme: isDark ? "dark" : "light" }}>
          {options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {hint && <Hint c={hint}/>}
    </div>
  );
}

function FldColor({ label, name, value, onChange, hint }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  return (
    <div>
      <label style={S.lbl}>{label}</label>
      <div style={{ ...S.wrap, gap:10, padding:"7px 14px" }}>
        <input type="color" name={name} value={value} onChange={onChange}
          style={{ width:32, height:32, border:"none", borderRadius:6, cursor:"pointer", WebkitTapHighlightColor:"transparent", padding:0 }} />
        <input type="text" name={name} value={value} onChange={onChange}
          maxLength={7} placeholder="#000000" style={{ ...S.inp, fontFamily:"monospace" }} />
        <div style={{ width:22, height:22, borderRadius:6, flexShrink:0, background:value, border:`1.5px solid ${T.inputBdr}` }} />
      </div>
      {hint && <Hint c={hint}/>}
    </div>
  );
}

function Toggle({ label, name, checked, onChange, hint }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16 }}>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:13, fontWeight:700, color:T.textPrimary, fontFamily:BRAND.font }}>{label}</div>
        {hint && <div style={{ fontSize:11, color:T.textMuted, marginTop:2, fontFamily:BRAND.font }}>{hint}</div>}
      </div>
      <div onClick={() => onChange({ target:{ name, value: !checked } })} style={{
        width:44, height:24, borderRadius:12, cursor:"pointer", WebkitTapHighlightColor:"transparent", flexShrink:0, position:"relative", transition:"all 0.2s",
        background: checked ? `linear-gradient(135deg,${T.yellow},#ffd700)` : T.glass,
        border: checked ? "none" : `1.5px solid ${T.inputBdr}`,
        boxShadow: checked ? "0 2px 8px rgba(255,200,0,0.35)" : "none",
      }}>
        <div style={{ position:"absolute", top:2, left:checked?22:2, width:20, height:20, borderRadius:"50%", transition:"left 0.2s",
          background: checked ? T.blue : T.textMuted, boxShadow:"0 1px 4px rgba(0,0,0,0.18)" }} />
      </div>
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────
function Pill({ active }) {
  return <span style={{ padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:700,
    background: active ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.09)",
    color:      active ? "#059669" : "#dc2626",
    border:     `1px solid ${active ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.20)"}` }}>
    {active ? "Active" : "Inactive"}
  </span>;
}

// ══════════════════════════════════════════════════════════════════════════════
//  GENERIC LOOKUP PANEL  (reused for all 4 lookup types)
// ══════════════════════════════════════════════════════════════════════════════
function LookupPanel({ title, subtitle, icon, apiPath, idKey, blank, columns, formFields, onDataLoaded }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [msg,          setMsg]          = useState(null);
  const [editing,      setEditing]      = useState(null);  // id | "new" | null
  const [form,         setForm]         = useState({ ...blank });
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}${apiPath}`);
      const d = await r.json();
      const data = d.data || [];
      setRows(data);
      if (onDataLoaded) onDataLoaded(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [apiPath]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    const f = {};
    formFields.forEach(ff => { f[ff.key] = row[ff.key] !== undefined && row[ff.key] !== null ? String(row[ff.key]) : ""; });
    setEditing(row[idKey]); setForm(f); setMsg(null);
  };
  const startNew  = () => { setEditing("new"); setForm({ ...blank }); setMsg(null); };
  const cancel    = () => { setEditing(null);  setForm({ ...blank }); };

  const fieldChange = (e) => { const { name, value } = e.target; setForm(p => ({ ...p, [name]: value })); };

  const save = async () => {
    const url    = editing === "new" ? `${BASE}${apiPath}` : `${BASE}${apiPath}/${editing}`;
    const method = editing === "new" ? "POST" : "PUT";
    try {
      const r = await fetch(url, { method, headers:{ "Content-Type":"application/json" }, body: JSON.stringify(form) });
      const d = await r.json();
      setMsg({ ok: d.success, text: d.message });
      if (d.success) { cancel(); load(); }
    } catch { setMsg({ ok:false, text:"Network error" }); }
  };

  const deactivate = async (id, row) => {
    const label = row[formFields[0].key] || id;
    if (!window.confirm(`Deactivate "${label}"?\n\nIt will be hidden from dropdowns but all historical data is preserved.`)) return;
    try {
      const r = await fetch(`${BASE}${apiPath}/${id}`, { method:"DELETE" });
      const d = await r.json();
      setMsg({ ok: d.success, text: d.message });
      load();
    } catch { setMsg({ ok:false, text:"Network error" }); }
  };

  const reactivate = async (row) => {
    const body = {};
    formFields.forEach(ff => { body[ff.key] = row[ff.key]; });
    body.active = 1;
    try {
      const r = await fetch(`${BASE}${apiPath}/${row[idKey]}`, { method:"PUT", headers:{ "Content-Type":"application/json" }, body: JSON.stringify(body) });
      const d = await r.json();
      setMsg({ ok: d.success, text: d.message });
      load();
    } catch { setMsg({ ok:false, text:"Network error" }); }
  };

  const active   = rows.filter(r => r.active !== 0 && r.active !== false);
  const inactive = rows.filter(r => r.active === 0  || r.active === false);

  return (
    <div style={{ marginBottom:40 }}>
      {/* Header row */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:10, flexShrink:0,
            background:`linear-gradient(135deg,${T.yellow},#fff3a0)`,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
            {React.cloneElement(icon, { size:15, color:T.blue })}
          </div>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:T.blue, fontFamily:T.font }}>{title}</div>
            <div style={{ fontSize:11, color:T.textMuted, fontFamily:T.font }}>{subtitle}</div>
          </div>
        </div>
        <button onClick={startNew} style={S.yBtn(true)}><FaPlus size={10}/> Add New</button>
      </div>

      {/* Toast */}
      {msg && (
        <div style={mkToast(msg.ok)} onClick={() => setMsg(null)}>
          {msg.ok ? <FaCheckCircle size={13}/> : <FaExclamationCircle size={13}/>}
          {msg.text}
          <FaTimes size={10} style={{ marginLeft:"auto", opacity:0.5 }}/>
        </div>
      )}

      {/* Inline form */}
      {editing !== null && (
        <div style={{ background:"rgba(247,206,11,0.06)", border:"1.5px solid rgba(247,206,11,0.40)", borderRadius:12, padding:"18px 20px", marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:800, color:T.blue, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>
            {editing === "new" ? "✦ Add New" : "✎ Editing"} {title.replace(/s$/, "")}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))", gap:"10px 18px", marginBottom:14 }}>
            {formFields.map(f => (
              <div key={f.key}>
                <label style={{ ...S.lbl, marginBottom:5 }}>{f.label}</label>
                {f.type === "select" ? (
                  <div style={S.wrap}>
                    <select name={f.key} value={form[f.key]||""} onChange={fieldChange}
                      style={{ ...S.inp, cursor:"pointer", WebkitTapHighlightColor:"transparent", colorScheme: isDark ? "dark" : "light" }} disabled={f.disabled}>
                      {(f.options||[]).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                ) : f.type === "color" ? (
                  <div style={{ ...S.wrap, gap:8, padding:"7px 12px" }}>
                    <input type="color" name={f.key} value={form[f.key]||"#3b82f6"} onChange={fieldChange}
                      style={{ width:28, height:28, border:"none", borderRadius:5, cursor:"pointer", WebkitTapHighlightColor:"transparent", padding:0 }}/>
                    <input type="text" name={f.key} value={form[f.key]||""} onChange={fieldChange}
                      maxLength={7} style={{ ...S.inp, fontFamily:"monospace", fontSize:13 }}/>
                    <div style={{ width:20, height:20, borderRadius:5, flexShrink:0,
                      background:form[f.key]||"#3b82f6", border:"1px solid rgba(0,0,0,0.12)" }}/>
                  </div>
                ) : (
                  <div style={S.wrap}>
                    <input type={f.type||"text"} name={f.key} value={form[f.key]||""} onChange={fieldChange}
                      placeholder={f.placeholder} disabled={f.disabled}
                      style={{ ...S.inp, ...(f.disabled ? { opacity:0.45, cursor:"not-allowed" } : {}) }}/>
                  </div>
                )}
                {f.hint && <p style={{ fontSize:10, color:T.textMuted, margin:"3px 0 0", fontFamily:BRAND.font }}>{f.hint}</p>}
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={save}   style={S.yBtn(true)}><FaCheck size={10}/> Save</button>
            <button onClick={cancel} style={S.bBtn(true)}><FaTimes size={10}/> Cancel</button>
          </div>
        </div>
      )}

      {/* Data table */}
      <div style={{ background:T.glass, backdropFilter:BRAND.blur, WebkitBackdropFilter:BRAND.blur, border:T.glassBdr, borderRadius:12, overflow:"hidden", boxShadow:T.glassSh }}>
        {loading ? (
          <div style={{ padding:32, textAlign:"center", color:T.textMuted, fontSize:13 }}>Loading…</div>
        ) : active.length === 0 ? (
          <div style={{ padding:32, textAlign:"center", color:T.textMuted, fontSize:13 }}>
            No active records. Click <strong style={{ color:T.blue }}>Add New</strong> above.
          </div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:T.rowOdd, borderBottom:`2px solid ${T.divider}` }}>
                {columns.map(c => (
                  <th key={c.key} style={{ padding:"10px 14px", fontSize:10, fontWeight:800, color:T.textMuted,
                    textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"left", fontFamily:T.font, whiteSpace:"nowrap" }}>
                    {c.label}
                  </th>
                ))}
                <th style={{ padding:"10px 14px", fontSize:10, fontWeight:800, color:T.textMuted,
                  textTransform:"uppercase", letterSpacing:"0.07em", textAlign:"right", fontFamily:T.font }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {active.map((row, i) => {
                const isEditing = editing === row[idKey];
                return (
                  <tr key={row[idKey]}
                    style={{ borderBottom:"1px solid rgba(3,78,123,0.05)", transition:"background 0.12s",
                      background: isEditing ? "rgba(247,206,11,0.07)" : i%2===0 ? T.rowEven : T.rowOdd }}
                    onMouseEnter={e => { if (!isEditing) e.currentTarget.style.background=T.glass; }}
                    onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background=i%2===0?T.rowEven:T.rowOdd; }}
                  >
                    {columns.map(c => (
                      <td key={c.key} style={{ padding:"11px 14px", fontSize:13, fontFamily:T.font, color:T.textPrimary }}>
                        {c.render ? c.render(row) : row[c.key]}
                      </td>
                    ))}
                    <td style={{ padding:"11px 14px", textAlign:"right" }}>
                      <div style={{ display:"flex", justifyContent:"flex-end", gap:6 }}>
                        <button onClick={() => startEdit(row)} style={S.gBtn}><FaEdit size={10}/> Edit</button>
                        <button onClick={() => deactivate(row[idKey], row)} style={S.dBtn}><FaTrash size={10}/> Deactivate</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Inactive accordion */}
      {inactive.length > 0 && (
        <div style={{ marginTop:8 }}>
          <button onClick={() => setShowInactive(s => !s)}
            style={{ background:"none", border:"none", cursor:"pointer", WebkitTapHighlightColor:"transparent", fontSize:12, color:T.textMuted, fontFamily:T.font, display:"flex", alignItems:"center", gap:5, padding:"4px 2px" }}>
            {showInactive ? <FaChevronUp size={9}/> : <FaChevronDown size={9}/>}
            {inactive.length} inactive record{inactive.length !== 1 ? "s" : ""} — click to view
          </button>
          {showInactive && (
            <div style={{ background:T.glass, backdropFilter:BRAND.blur, WebkitBackdropFilter:BRAND.blur, border:T.glassBdr, borderRadius:18, boxShadow:T.glassSh, padding:"10px 14px", marginTop:6, opacity:0.72 }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <tbody>
                  {inactive.map(row => (
                    <tr key={row[idKey]} style={{ borderBottom:"1px solid rgba(3,78,123,0.05)" }}>
                      {columns.map(c => (
                        <td key={c.key} style={{ padding:"8px 12px", fontSize:12, color:T.textMuted, fontFamily:T.font }}>
                          {c.render ? c.render(row) : row[c.key]}
                        </td>
                      ))}
                      <td style={{ padding:"8px 12px", textAlign:"right" }}>
                        <button onClick={() => reactivate(row)} style={S.sBtn}><FaCheck size={9}/> Reactivate</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  LOOKUPS TAB  — houses all four CRUD panels with sub-tabs
// ══════════════════════════════════════════════════════════════════════════════
function LookupsTab() {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [subTab,     setSubTab]     = useState("statuses");
  const [categories, setCategories] = useState([]);

  const handleCatLoad = (data) => setCategories(data.filter(c => c.active !== 0));

  const SUB = [
    { id:"statuses",    label:"Student Statuses",       icon:<FaUserTag/>       },
    { id:"levels",      label:"Skill Levels",           icon:<FaGraduationCap/> },
    { id:"agecats",     label:"Age Categories",         icon:<FaBirthdayCake/>  },
    { id:"roles",       label:"Playing Roles",          icon:<FaBaseballBall/>  },
    { id:"programs",    label:"Programs",               icon:<FaLayerGroup/>    },
    { id:"categories",  label:"Performance Categories", icon:<FaChartBar/>      },
    { id:"indicators",  label:"Performance Indicators", icon:<FaListUl/>        },
    { id:"eval_cats",   label:"Evaluation KPIs (Cat)",   icon:<FaChartBar/>      },
    { id:"eval_metrics",label:"Evaluation KPIs (Met)",   icon:<FaListUl/>        },
  ];

  const catOpts = [["","— Select Category —"], ...categories.map(c => [String(c.category_id), c.category_name])];
  const [evalCats, setEvalCats] = useState([]);
  const handleEvalCatLoad = (data) => setEvalCats(data.filter(c => c.is_active !== 0));
  const evalCatOpts = [["","— Select Evaluation Category —"], ...evalCats.map(c => [String(c.id), c.name])];

  return (
    <div>
      {/* Info strip */}
      <div style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"12px 16px", borderRadius:10, marginBottom:20,
        background:"rgba(3,78,123,0.05)", border:"1px solid rgba(3,78,123,0.12)", fontFamily:T.font }}>
        <FaInfoCircle size={14} color={T.blue} style={{ marginTop:1, flexShrink:0 }}/>
        <div style={{ fontSize:12, color:"#44556a", lineHeight:1.6 }}>
          These are the <strong>master reference values</strong> used across all dropdowns in the application.
          Changes take effect immediately. <strong>Deactivating</strong> hides a value from new records but preserves all historical data.
        </div>
      </div>

      {/* Sub-tab pills */}
      <div style={{ display:"flex", gap:8, marginBottom:24, flexWrap:"wrap" }}>
        {SUB.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{
            padding:"8px 15px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer", WebkitTapHighlightColor:"transparent",
            display:"flex", alignItems:"center", gap:6, fontFamily:T.font, transition:"all 0.15s",
            background: subTab===t.id ? `linear-gradient(135deg,${T.yellow},#fff3a0)` : T.glass,
            color:      subTab===t.id ? "#02304f" : T.textLabel,
            border:     subTab===t.id ? "1.5px solid rgba(247,206,11,0.55)" : "1.5px solid rgba(0,53,128,0.12)",
            boxShadow:  subTab===t.id ? "0 3px 10px rgba(247,206,11,0.22)" : "none",
          }}>
            {React.cloneElement(t.icon, { size:11 })} {t.label}
          </button>
        ))}
      </div>

      {/* ── Student Statuses ── */}
      {subTab === "statuses" && (
        <LookupPanel
          title="Student Active Statuses"
          subtitle="Status options used on student records across all forms (Active, Suspended, Cancelled)"
          icon={<FaUserTag/>}
          apiPath="/config/lookups/student-statuses"
          idKey="status_id"
          blank={{ status_name:"", status_value:"", description:"", sort_order:"99" }}
          columns={[
            { key:"status_value", label:"DB Value", render: r => <code style={{ background:"rgba(3,78,123,0.08)", padding:"2px 8px", borderRadius:6, fontSize:12 }}>{r.status_value}</code> },
            { key:"status_name",  label:"Status Name", render: r => <strong style={{ color:T.blue }}>{r.status_name}</strong> },
            { key:"description",  label:"Description", render: r => <span style={{ color:"#666", fontSize:12 }}>{r.description||"—"}</span> },
            { key:"sort_order",   label:"Order",       render: r => <span style={{ color:T.textMuted }}>{r.sort_order}</span> },
            { key:"active",       label:"Status",      render: r => <Pill active={r.active}/> },
          ]}
          formFields={[
            { key:"status_name",  label:"Status Name",       type:"text",   placeholder:"e.g. Active" },
            { key:"status_value", label:"Integer Value (DB)", type:"number", placeholder:"e.g. 1", hint:"Stored in students table. Cannot change after creation." },
            { key:"sort_order",   label:"Sort Order",        type:"number", placeholder:"1" },
            { key:"description",  label:"Description",       type:"text",   placeholder:"Brief description" },
          ]}
        />
      )}

      {/* ── Skill Levels ── */}
      {subTab === "levels" && (
        <LookupPanel
          title="Skill Levels"
          subtitle="Player levels shown in Student Master, Batch enrolment and the Promotion Readiness report"
          icon={<FaGraduationCap/>}
          apiPath="/config/lookups/skill-levels"
          idKey="level_id"
          blank={{ level_name:"", level_order:"99", color_hex:"#3b82f6", description:"" }}
          columns={[
            { key:"level_order", label:"Order",  render: r => <span style={{ fontWeight:700, color:T.textMuted }}>{r.level_order}</span> },
            { key:"color_hex",   label:"Colour", render: r => (
              <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                <div style={{ width:18, height:18, borderRadius:5, background:r.color_hex, border:"1px solid rgba(0,0,0,0.12)" }}/>
                <code style={{ fontSize:11, color:"#555" }}>{r.color_hex}</code>
              </div>
            )},
            { key:"level_name",  label:"Level Name", render: r => (
              <span style={{ fontWeight:700, padding:"3px 10px", borderRadius:12,
                color:r.color_hex, background:r.color_hex+"22", border:`1px solid ${r.color_hex}55` }}>
                {r.level_name}
              </span>
            )},
            { key:"description", label:"Description", render: r => <span style={{ color:"#666", fontSize:12 }}>{r.description||"—"}</span> },
            { key:"active",      label:"Status",      render: r => <Pill active={r.active}/> },
          ]}
          formFields={[
            { key:"level_name",  label:"Level Name",   type:"text",   placeholder:"e.g. Elite" },
            { key:"level_order", label:"Sort Order",   type:"number", placeholder:"4" },
            { key:"color_hex",   label:"Badge Colour", type:"color" },
            { key:"description", label:"Description",  type:"text",   placeholder:"Brief description" },
          ]}
        />
      )}

      {/* ── Programs ── */}
      {subTab === "agecats" && (
        <LookupPanel
          title="Age Categories"
          subtitle="Age bands auto-assigned to students based on date of birth — used in Student Master, rosters and reports"
          icon={<FaBirthdayCake/>}
          apiPath="/config/lookups/age-categories"
          idKey="category_id"
          blank={{ category_code:"", category_name:"", min_age:"", max_age:"", sort_order:"99", description:"" }}
          columns={[
            { key:"sort_order",    label:"Order",  render: r => <span style={{ fontWeight:700, color:T.textMuted }}>{r.sort_order}</span> },
            { key:"category_code", label:"Code",   render: r => (
              <span style={{ fontWeight:800, padding:"3px 12px", borderRadius:12, fontSize:13,
                color:T.blue, background:"rgba(3,78,123,0.09)", border:"1px solid rgba(3,78,123,0.18)" }}>
                {r.category_code}
              </span>
            )},
            { key:"category_name", label:"Name",        render: r => <strong style={{ color:T.blue }}>{r.category_name}</strong> },
            { key:"min_age",       label:"Min Age (≥)",  render: r => <span style={{ color:"#555" }}>{r.min_age ?? "—"}</span> },
            { key:"max_age",       label:"Max Age (<)",  render: r => <span style={{ color:"#555" }}>{r.max_age ?? "—"}</span> },
            { key:"description",   label:"Description",  render: r => <span style={{ color:"#666", fontSize:12 }}>{r.description||"—"}</span> },
            { key:"active",        label:"Status",       render: r => <Pill active={r.active}/> },
          ]}
          formFields={[
            { key:"category_code", label:"Code (e.g. U13)",  type:"text",   placeholder:"U13" },
            { key:"category_name", label:"Display Name",     type:"text",   placeholder:"Under 13" },
            { key:"min_age",       label:"Min Age (inclusive)", type:"number", placeholder:"11", hint:"Leave blank for no lower bound" },
            { key:"max_age",       label:"Max Age (exclusive)", type:"number", placeholder:"13", hint:"Leave blank for no upper bound" },
            { key:"sort_order",    label:"Sort Order",       type:"number", placeholder:"3" },
            { key:"description",   label:"Description",      type:"text",   placeholder:"Players aged 11 to under 13" },
          ]}
        />
      )}

      {subTab === "roles" && (
        <LookupPanel
          title="Playing Roles"
          subtitle="Cricket playing roles selectable on student profiles — grouped by Batting, Bowling, All-Rounder, Wicketkeeper"
          icon={<FaBaseballBall/>}
          apiPath="/config/lookups/playing-roles"
          idKey="role_id"
          blank={{ role_name:"", role_group:"Batting", sort_order:"99", description:"" }}
          columns={[
            { key:"sort_order", label:"Order",  render: r => <span style={{ fontWeight:700, color:T.textMuted }}>{r.sort_order}</span> },
            { key:"role_group", label:"Group",  render: r => (
              <span style={{ padding:"2px 9px", borderRadius:8, fontSize:11, fontWeight:700,
                background:"rgba(3,78,123,0.08)", color:T.blue }}>{r.role_group}</span>
            )},
            { key:"role_name",  label:"Role Name",   render: r => <strong style={{ color:"#1a2840" }}>{r.role_name}</strong> },
            { key:"description",label:"Description", render: r => <span style={{ color:"#666", fontSize:12 }}>{r.description||"—"}</span> },
            { key:"active",     label:"Status",      render: r => <Pill active={r.active}/> },
          ]}
          formFields={[
            { key:"role_name",  label:"Role Name",   type:"text",   placeholder:"e.g. Opening Batter" },
            { key:"role_group", label:"Group",       type:"select", options:[
                ["","— Select Group —"],
                ["Batting","Batting"],
                ["Bowling","Bowling"],
                ["All-Rounder","All-Rounder"],
                ["Wicketkeeper","Wicketkeeper"],
              ]
            },
            { key:"sort_order", label:"Sort Order",  type:"number", placeholder:"99" },
            { key:"description",label:"Description", type:"text",   placeholder:"Brief description" },
          ]}
        />
      )}

      {/* ── Programs ── */}
      {subTab === "programs" && (
        <LookupPanel
          title="Programs"
          subtitle="Training programs linked to batches — defines days/week and sessions/month. Used in Batch Management for day-selection limits and enrollment."
          icon={<FaLayerGroup/>}
          apiPath="/config/lookups/programs"
          idKey="program_id"
          blank={{ program_name:"", days_per_week:"1", sessions_per_month:"4", description:"" }}
          columns={[
            { key:"program_id",         label:"ID",     render: r => <code style={{ background:"rgba(3,78,123,0.08)", padding:"2px 7px", borderRadius:6, fontSize:12 }}>{r.program_id}</code> },
            { key:"program_name",       label:"Program Name", render: r => <strong style={{ color:T.blue }}>{r.program_name}</strong> },
            { key:"days_per_week",      label:"Days / Week", render: r => (
              <span style={{ fontWeight:800, padding:"3px 12px", borderRadius:12, fontSize:13,
                color:T.blue, background:"rgba(3,78,123,0.09)", border:"1px solid rgba(3,78,123,0.18)" }}>
                {r.days_per_week}×/wk
              </span>
            )},
            { key:"sessions_per_month", label:"Sessions / Month", render: r => (
              <span style={{ color:"#555", fontWeight:600 }}>{r.sessions_per_month} sessions</span>
            )},
            { key:"description",        label:"Description", render: r => <span style={{ color:"#666", fontSize:12 }}>{r.description||"—"}</span> },
            { key:"active",             label:"Status",      render: r => <Pill active={r.active}/> },
          ]}
          formFields={[
            { key:"program_name",       label:"Program Name",        type:"text",   placeholder:"e.g. 3-Day Program" },
            { key:"days_per_week",      label:"Days per Week",       type:"number", placeholder:"3",  hint:"Limits how many schedule days can be selected when creating a batch" },
            { key:"sessions_per_month", label:"Sessions per Month",  type:"number", placeholder:"12", hint:"Shown on batch cards and enrollment summaries" },
            { key:"description",        label:"Description",         type:"text",   placeholder:"Optional description" },
          ]}
        />
      )}

      {/* ── Performance Categories ── */}
      {subTab === "categories" && (
        <LookupPanel
          title="Performance Categories"
          subtitle="Groups that organise performance indicators: Batting, Bowling, Fielding, Fitness"
          icon={<FaChartBar/>}
          apiPath="/config/lookups/performance-categories"
          idKey="category_id"
          blank={{ category_name:"", description:"" }}
          onDataLoaded={handleCatLoad}
          columns={[
            { key:"category_id",   label:"ID",       render: r => <code style={{ background:"rgba(3,78,123,0.08)", padding:"2px 7px", borderRadius:6, fontSize:12 }}>{r.category_id}</code> },
            { key:"category_name", label:"Category", render: r => <strong style={{ color:T.blue }}>{r.category_name}</strong> },
            { key:"description",   label:"Description", render: r => <span style={{ color:"#666", fontSize:12 }}>{r.description||"—"}</span> },
            { key:"active",        label:"Status",   render: r => <Pill active={r.active}/> },
          ]}
          formFields={[
            { key:"category_name", label:"Category Name", type:"text", placeholder:"e.g. Batting" },
            { key:"description",   label:"Description",   type:"text", placeholder:"e.g. Batting skills — stance, shot selection, footwork" },
          ]}
        />
      )}

      {/* ── Performance Indicators ── */}
      {subTab === "indicators" && (
        <LookupPanel
          title="Performance Indicators"
          subtitle="Scored metrics within each category (max 10 pts each) — e.g. Batting Stance, Catching, Stamina"
          icon={<FaListUl/>}
          apiPath="/config/lookups/performance-indicators"
          idKey="indicator_id"
          blank={{ indicator_name:"", category_id:"", description:"", max_score:"10" }}
          columns={[
            { key:"category_name",  label:"Category", render: r => (
              <span style={{ padding:"2px 8px", borderRadius:8, fontSize:11, fontWeight:700,
                background:"rgba(3,78,123,0.08)", color:T.blue }}>{r.category_name}</span>
            )},
            { key:"indicator_name", label:"Indicator",   render: r => <strong style={{ color:"#1a2840" }}>{r.indicator_name}</strong> },
            { key:"max_score",      label:"Max Score",   render: r => <span style={{ fontWeight:700, color:T.blue }}>{r.max_score}</span> },
            { key:"description",    label:"Description", render: r => <span style={{ color:"#666", fontSize:12 }}>{r.description||"—"}</span> },
            { key:"active",         label:"Status",      render: r => <Pill active={r.active}/> },
          ]}
          formFields={[
            { key:"indicator_name", label:"Indicator Name", type:"text",   placeholder:"e.g. Batting Stance" },
            { key:"category_id",    label:"Category",       type:"select", options: catOpts },
            { key:"max_score",      label:"Max Score",      type:"number", placeholder:"10" },
            { key:"description",    label:"Description",    type:"text",   placeholder:"Brief description" },
          ]}
        />
      )}

      {/* ── Coach Evaluation Categories ── */}
      {subTab === "eval_cats" && (
        <LookupPanel
          title="Evaluation KPI Categories"
          subtitle="Weighted categories for Coach Evaluation (e.g. Technical Skills 20%, Communication 15%)"
          icon={<FaChartBar/>}
          apiPath="/evaluations/categories"
          idKey="id"
          blank={{ name:"", weight:"20", display_order:"0" }}
          onDataLoaded={handleEvalCatLoad}
          columns={[
            { key:"display_order", label:"Order",   render: r => <span style={{ fontWeight:700, color:T.textMuted }}>{r.display_order}</span> },
            { key:"name",          label:"Category", render: r => <strong style={{ color:T.blue }}>{r.name}</strong> },
            { key:"weight",        label:"Weight (%)",render: r => <span style={{ fontWeight:900, color:T.yellow }}>{r.weight}%</span> },
            { key:"is_active",     label:"Status",   render: r => <Pill active={r.is_active}/> },
          ]}
          formFields={[
            { key:"name",          label:"Category Name", type:"text",   placeholder:"e.g. Technical Skills" },
            { key:"weight",        label:"Weight (0-100)", type:"number", placeholder:"20" },
            { key:"display_order", label:"Display Order", type:"number", placeholder:"1" },
          ]}
        />
      )}

      {/* ── Coach Evaluation Metrics ── */}
      {subTab === "eval_metrics" && (
        <LookupPanel
          title="Evaluation KPI Metrics"
          subtitle="Specific metrics graded under each evaluation category (e.g. Technique Explanation, Communication Quality)"
          icon={<FaListUl/>}
          apiPath="/evaluations/metrics"
          idKey="id"
          blank={{ name:"", category_id:"", display_order:"0" }}
          columns={[
            { key:"category_name",  label:"Category", render: r => (
              <span style={{ padding:"2px 8px", borderRadius:8, fontSize:11, fontWeight:700,
                background:"rgba(247,206,11,0.08)", color:T.blue }}>{r.category_name}</span>
            )},
            { key:"name",           label:"Metric Name", render: r => <strong style={{ color:"#1a2840" }}>{r.name}</strong> },
            { key:"display_order",  label:"Order",       render: r => <span style={{ fontWeight:700, color:T.textMuted }}>{r.display_order}</span> },
            { key:"is_active",      label:"Status",      render: r => <Pill active={r.is_active}/> },
          ]}
          formFields={[
            { key:"name",           label:"Metric Name",    type:"text",   placeholder:"e.g. Demonstration Quality" },
            { key:"category_id",    label:"Category",       type:"select", options: evalCatOpts },
            { key:"display_order",  label:"Display Order",  type:"number", placeholder:"1" },
          ]}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  SETTINGS TABS (unchanged from existing — org, branding, reports, system)
// ══════════════════════════════════════════════════════════════════════════════
function OrgTab({ form, onChange }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px 28px" }}>
      <SectionDiv>Identity</SectionDiv>
      <FldText label="Organisation Name" icon={<FaBuilding/>}  name="org_name"    value={form.org_name}    onChange={onChange} placeholder="Sixers Sports Center" />
      <FldText label="Tagline / Motto"   icon={<FaAlignLeft/>} name="org_tagline" value={form.org_tagline} onChange={onChange} placeholder="Short descriptor shown on login page" />
      <SectionDiv>Contact Information</SectionDiv>
      <FldText label="Email Address" icon={<FaEnvelope/>} name="org_email" value={form.org_email} onChange={onChange} placeholder="info@yourorg.com" />
      <FldText label="Phone Number"  icon={<FaBuilding/>} name="org_phone" value={form.org_phone} onChange={onChange} placeholder="+1 000-000-0000" />
      <div style={{ gridColumn:"1/-1" }}>
        <label style={S.lbl}>Street Address</label>
        <div style={S.wrap}><textarea name="org_address" value={form.org_address} onChange={onChange}
          placeholder="Full mailing address" style={{ ...S.inp, resize:"vertical", minHeight:68, paddingTop:2 }} /></div>
      </div>
    </div>
  );
}

function BrandingTab({ form, onChange, onLogoChange, logoPreview }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  const fileRef = useRef(null);
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px 28px" }}>
      <SectionDiv>Logo</SectionDiv>
      <div style={{ gridColumn:"1/-1", display:"flex", alignItems:"center", gap:22 }}>
        <div style={{ width:90, height:90, borderRadius:14, flexShrink:0, background:"rgba(0,53,128,0.05)",
          border:"2px dashed rgba(0,53,128,0.18)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
          {logoPreview ? <img src={logoPreview} alt="Logo" style={{ width:"100%", height:"100%", objectFit:"contain" }}/> : <FaImage size={28} style={{ color:T.textMuted }}/>}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600, color:T.blue, marginBottom:8, fontFamily:T.font }}>Upload Organisation Logo</div>
          <div style={{ fontSize:11, color:T.textMuted, marginBottom:10, fontFamily:T.font }}>PNG or SVG, min 200×200px, transparent bg. Max 2MB.</div>
          <div style={{ display:"flex", gap:8 }}>
            <button type="button" onClick={() => fileRef.current?.click()} style={S.yBtn(true)}><FaUpload size={11}/> Choose File</button>
            {logoPreview && <button type="button" onClick={() => onLogoChange(null)} style={S.bBtn(true)}><FaTimesCircle size={11}/> Remove</button>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }}
            onChange={e => { const f=e.target.files[0]; if(!f) return; const r=new FileReader(); r.onload=ev=>onLogoChange(ev.target.result); r.readAsDataURL(f); }} />
        </div>
      </div>
      <SectionDiv>Colour Palette</SectionDiv>
      <FldColor label="Primary Colour"   name="primary_color"   value={form.primary_color}   onChange={onChange} hint="Sidebar & headers" />
      <FldColor label="Secondary Colour" name="secondary_color" value={form.secondary_color} onChange={onChange} hint="Accent buttons & active states" />
      <FldColor label="Accent Colour"    name="accent_color"    value={form.accent_color}    onChange={onChange} hint="Dark overlay & deep headers" />
      <SectionDiv>Typography & Layout</SectionDiv>
      <FldSelect label="Font Family" icon={<FaFont/>} name="font_family" value={form.font_family} onChange={onChange}
        options={[["Segoe UI","Segoe UI (Default)"],["Inter","Inter"],["Roboto","Roboto"],["Open Sans","Open Sans"],["Poppins","Poppins"],["Lato","Lato"]]}
        hint="Applied to all text across the app" />
      <FldSelect label="Sidebar Opacity" icon={<FaPalette/>} name="sidebar_opacity" value={form.sidebar_opacity} onChange={onChange}
        options={[["0.30","30% — Very light"],["0.40","40% — Light"],["0.50","50% — Medium"],["0.65","65% — Solid"],["0.85","85% — Dense"]]}
        hint="Transparency of the navigation sidebar" />
    </div>
  );
}

function ReportsTab({ form, onChange }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px 28px" }}>
      <SectionDiv>Report Header &amp; Footer</SectionDiv>
      <div style={{ gridColumn:"1/-1" }}>
        <label style={S.lbl}>Report Header Text</label>
        <div style={S.wrap}><textarea name="report_header" value={form.report_header} onChange={onChange}
          placeholder="Custom text shown at the top of every report" style={{ ...S.inp, resize:"vertical", minHeight:60, paddingTop:2 }} /></div>
      </div>
      <div style={{ gridColumn:"1/-1" }}>
        <label style={S.lbl}>Report Footer Text</label>
        <div style={S.wrap}><textarea name="report_footer" value={form.report_footer} onChange={onChange}
          placeholder="e.g. Confidential — Sixers Sports Center" style={{ ...S.inp, resize:"vertical", minHeight:60, paddingTop:2 }} /></div>
      </div>
      <SectionDiv>Report Options</SectionDiv>
      <FldSelect label="Colour Scheme" icon={<FaPalette/>} name="report_color_scheme" value={form.report_color_scheme} onChange={onChange}
        options={[["blue","Brand Blue"],["mono","Monochrome"],["green","Green"],["red","Red"],["purple","Purple"]]} hint="Table headers and accents" />
      <FldSelect label="Items Per Page" icon={<FaFileAlt/>} name="items_per_page" value={form.items_per_page} onChange={onChange}
        options={[["10","10"],["25","25 (default)"],["50","50"],["100","100"]]} hint="Default rows in all tables" />
      <div style={{ gridColumn:"1/-1" }}>
        <Toggle label="Show Logo on Reports" name="report_show_logo" checked={form.report_show_logo} onChange={onChange}
          hint="Print the organisation logo in the top-left corner of every report" />
      </div>
    </div>
  );
}

function SystemTab({ form, onChange }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px 28px" }}>
      <SectionDiv>Locale &amp; Formatting</SectionDiv>
      <FldSelect label="Date Format" icon={<FaClock/>} name="date_format" value={form.date_format} onChange={onChange}
        options={[["MM/DD/YYYY","MM/DD/YYYY (US)"],["DD/MM/YYYY","DD/MM/YYYY (UK/AU)"],["YYYY-MM-DD","YYYY-MM-DD (ISO)"]]} hint="Applied to all dates" />
      <FldSelect label="Time Format" icon={<FaClock/>} name="time_format" value={form.time_format} onChange={onChange}
        options={[["12h","12-hour (AM/PM)"],["24h","24-hour"]]} />
      <FldSelect label="Batch Start Hour" icon={<FaClock/>} name="batch_start_hour" value={form.batch_start_hour||"06"} onChange={onChange}
        options={[["04","04:00"],["05","05:00"],["06","06:00 (Default)"],["07","07:00"],["08","08:00"],["09","09:00"],["10","10:00"]]}
        hint="Earliest slot on the batch time-bar" />
      <FldSelect label="Batch End Hour" icon={<FaClock/>} name="batch_end_hour" value={form.batch_end_hour||"22"} onChange={onChange}
        options={[["18","18:00"],["19","19:00"],["20","20:00"],["21","21:00"],["22","22:00 (Default)"],["23","23:00"],["00","00:00"]]}
        hint="Latest slot on the batch time-bar" />
      <FldSelect label="Timezone" icon={<FaGlobe/>} name="timezone" value={form.timezone} onChange={onChange}
        options={[["America/New_York","Eastern (ET)"],["America/Chicago","Central (CT)"],["America/Los_Angeles","Pacific (PT)"],["Europe/London","London (GMT)"],["Asia/Kolkata","India (IST)"],["Asia/Singapore","Singapore (SGT)"],["Australia/Sydney","Sydney (AEST)"]]} hint="Scheduling and timestamps" />
      <FldSelect label="Currency" icon={<FaDollarSign/>} name="currency" value={form.currency} onChange={onChange}
        options={[["USD","USD — US Dollar"],["CAD","CAD — Canadian Dollar"],["GBP","GBP — British Pound"],["EUR","EUR — Euro"],["AUD","AUD — Australian Dollar"],["INR","INR — Indian Rupee"],["SGD","SGD — Singapore Dollar"]]} />
      <SectionDiv>Enrolment &amp; Capacity</SectionDiv>
      <FldSelect label="Max Students per Batch" icon={<FaCog/>} name="max_students_per_batch" value={form.max_students_per_batch||"20"} onChange={onChange}
        options={[["5","5"],["10","10"],["15","15"],["20","20 (Default)"],["25","25"],["30","30"],["50","50"],["0","Unlimited"]]} hint="Default cap for new batches" />
      <FldSelect label="Enrolment Mode" icon={<FaCog/>} name="enrollment_mode" value={form.enrollment_mode||"manual"} onChange={onChange}
        options={[["manual","Manual — admin assigns"],["open","Open — self-enroll"],["approval","Approval — request then confirm"]]} hint="How students are added to batches" />
      <SectionDiv>Security &amp; Session</SectionDiv>
      <FldSelect label="Session Timeout" icon={<FaClock/>} name="session_timeout" value={form.session_timeout} onChange={onChange}
        options={[["15","15 minutes"],["30","30 minutes"],["60","1 hour (default)"],["120","2 hours"],["480","8 hours"],["0","Never"]]} hint="Auto-logout after inactivity" />
      <FldSelect label="Password Min Length" icon={<FaLock/>} name="password_min_length" value={form.password_min_length||"6"} onChange={onChange}
        options={[["6","6 characters"],["8","8 characters"],["10","10 characters"],["12","12 characters"]]} />
      <div style={{ gridColumn:"1/-1", padding:"14px 18px", borderRadius:12, background:"rgba(220,0,0,0.05)", border:"1.5px solid rgba(220,0,0,0.15)" }}>
        <Toggle label="Maintenance Mode" name="maintenance_mode" checked={form.maintenance_mode} onChange={onChange}
          hint="When ON — only Admins can log in. All other users see a maintenance notice." />
      </div>
      <SectionDiv>Notifications</SectionDiv>
      <div style={{ gridColumn:"1/-1" }}>
        <Toggle label="Email Notifications" name="email_notifications_enabled" checked={form.email_notifications_enabled||false} onChange={onChange}
          hint="Send automated email alerts for enrolments, performance updates, and system events" />
      </div>
      <FldText label="Admin Notification Email" icon={<FaEnvelope/>} name="admin_notification_email" value={form.admin_notification_email||""} onChange={onChange}
        placeholder="admin@yourorg.com" hint="System alerts and summaries are sent here" />
    </div>
  );
}

function PreviewTab({ form, logoPreview }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  const sb  = form.primary_color   || "#034e7b";
  const yel = form.secondary_color || "#f7ce0b";
  const dk  = form.accent_color    || "#023a5c";
  const ff  = `'${form.font_family || "Segoe UI"}', system-ui, sans-serif`;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
      <div style={{ fontSize:13, fontWeight:700, color:T.textPrimary, fontFamily:BRAND.font }}>Login Page Preview</div>
      <div style={{ borderRadius:14, overflow:"hidden", border:T.glassBdr, background:`linear-gradient(140deg,${dk},${sb})`, padding:22, display:"flex", gap:22, minHeight:155 }}>
        <div style={{ flex:"0 0 44%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8 }}>
          {logoPreview ? <img src={logoPreview} alt="Logo" style={{ width:56 }}/> : <img src={SixersLogo} alt="Logo" style={{ width:56 }}/>}
          <div style={{ color:"white", fontSize:14, fontWeight:800, fontFamily:ff }}>{form.org_name||"Organisation"}</div>
          <div style={{ color:"rgba(255,255,255,0.55)", fontSize:11, fontFamily:ff }}>{form.org_tagline||"Tagline"}</div>
        </div>
        <div style={{ flex:1, background:"rgba(255,255,255,0.09)", borderRadius:10, border:"1px solid rgba(255,255,255,0.18)", padding:"14px 18px", display:"flex", flexDirection:"column", gap:9 }}>
          <div style={{ color:"white", fontSize:14, fontWeight:800, fontFamily:ff }}>Welcome back</div>
          {[1,2].map(i => <div key={i} style={{ height:26, borderRadius:6, background:"rgba(255,255,255,0.09)", border:"1px solid rgba(255,255,255,0.14)" }}/>)}
          <div style={{ height:30, borderRadius:7, background:`linear-gradient(135deg,${yel},${yel}cc)`, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontSize:11, fontWeight:800, color:dk, fontFamily:ff }}>Sign In</span>
          </div>
        </div>
      </div>
      <div style={{ ...S.card, padding:"18px 22px" }}>
        <div style={{ fontSize:13, fontWeight:700, color:T.textPrimary, marginBottom:10, fontFamily:BRAND.font }}>Typography — {form.font_family}</div>
        {[{s:22,w:800,t:"Page Heading — H1"},{s:16,w:700,t:"Section Heading — H2"},{s:13,w:600,t:"Field Label — Body Bold"},{s:13,w:400,t:`Body text · Date: ${form.date_format}`}].map((x,i)=>(
          <div key={i} style={{ fontSize:x.s, fontWeight:x.w, color:i===0?sb:T.textSub, fontFamily:ff, marginBottom:5 }}>{x.t}</div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id:"org",      label:"Organisation",  icon:FaBuilding  },
  { id:"branding", label:"Branding",      icon:FaPalette   },
  { id:"reports",  label:"Reports",       icon:FaFileAlt   },
  { id:"system",   label:"System",        icon:FaCog       },
  { id:"lookups",  label:"Lookup Values", icon:FaDatabase  },
  { id:"preview",  label:"Live Preview",  icon:FaEye       },
];

export default function ConfigMaster({ currentUserId }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const S = makeS(T);
  const { config: liveConfig, reloadConfig } = useAppConfig();
  const [tab,         setTab]         = useState("org");
  const [form,        setForm]        = useState({ ...liveConfig });
  const [logoPreview, setLogoPreview] = useState(liveConfig.logo_url || "");
  const [msg,         setMsg]         = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [dirty,       setDirty]       = useState(false);

  useEffect(() => { setForm({ ...liveConfig }); setLogoPreview(liveConfig.logo_url || ""); }, [liveConfig]);

  const handleChange     = e => { const { name, value } = e.target; setForm(p => ({ ...p, [name]: value })); setDirty(true); };
  const handleLogoChange = url => { setLogoPreview(url||""); setForm(p => ({ ...p, logo_url: url||"" })); setDirty(true); };
  const handleReset      = () => { setForm({ ...liveConfig }); setLogoPreview(liveConfig.logo_url||""); setDirty(false); setMsg(null); };

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}/config/`, { method:"POST", headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ ...form, updated_by: String(currentUserId||"system") }) });
      const d = await r.json();
      setMsg({ ok: d.success, text: d.message || "Error saving" });
      if (d.success) { setDirty(false); reloadConfig(); }
    } catch { setMsg({ ok:false, text:"Network error — could not reach server" }); }
    finally  { setSaving(false); }
  };

  const isLookup  = tab === "lookups";
  const isPreview = tab === "preview";
  const showSave  = !isLookup && !isPreview;

  return (
    <div style={{ minHeight:"100%", padding:"20px 28px 60px", boxSizing:"border-box", fontFamily:T.font, background:T.pageBg }}>
      <div style={{ maxWidth: isLookup ? 1140 : 940, margin:"0 auto" }}>

      {/* Page header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:22 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:42, height:42, borderRadius:12, flexShrink:0,
            background:`linear-gradient(135deg,${T.yellow},#fff3a0)`,
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow:"0 4px 14px rgba(255,200,0,0.30)" }}>
            <FaCog size={18} color={T.blue}/>
          </div>
          <div>
            <h2 style={{ fontSize:22, fontWeight:800, color:T.blue, margin:0 }}>Config Master</h2>
            <p style={{ margin:0, fontSize:12, color:T.textMuted, marginTop:1 }}>
              Branding, system settings and all application dropdown reference values
            </p>
          </div>
        </div>
        {showSave && (
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            {dirty && (
              <div style={{ padding:"5px 11px", borderRadius:8, fontSize:12, fontWeight:700,
                background:"rgba(255,200,0,0.14)", border:"1px solid rgba(255,200,0,0.40)", color:T.blue }}>
                Unsaved changes
              </div>
            )}
            <button onClick={handleReset} disabled={!dirty}
              style={{ ...S.bBtn(false), opacity:dirty?1:0.4, cursor:dirty?"pointer":"not-allowed" }}>
              <FaUndo size={11}/> Reset
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ ...S.yBtn(false), opacity:saving?0.7:1, cursor:saving?"not-allowed":"pointer" }}>
              <FaSave size={13}/> {saving ? "Saving…" : "Save All"}
            </button>
          </div>
        )}
      </div>

      {/* Toast (settings tabs only) */}
      {showSave && msg && (
        <div style={mkToast(msg.ok)} onClick={() => setMsg(null)}>
          {msg.ok ? <FaCheckCircle size={13}/> : <FaExclamationCircle size={13}/>} {msg.text}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display:"flex", gap:8, marginBottom:22, flexWrap:"wrap" }}>
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={mkTabBtn(tab === t.id, T)}>
              <Icon size={12}/> {t.label}
              {t.id === "lookups" && (
                <span style={{ padding:"1px 6px", borderRadius:10, fontSize:10, fontWeight:800,
                  background: tab==="lookups" ? "rgba(3,78,123,0.12)" : `rgba(247,206,11,0.65)`,
                  color: T.blue }}>7</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content card */}
      <div style={{ ...S.card }}>
        {tab === "org"      && <OrgTab      form={form} onChange={handleChange}/>}
        {tab === "branding" && <BrandingTab form={form} onChange={handleChange} onLogoChange={handleLogoChange} logoPreview={logoPreview}/>}
        {tab === "reports"  && <ReportsTab  form={form} onChange={handleChange}/>}
        {tab === "system"   && <SystemTab   form={form} onChange={handleChange}/>}
        {tab === "lookups"  && <LookupsTab/>}
        {tab === "preview"  && <PreviewTab  form={form} logoPreview={logoPreview}/>}

        {/* Save footer bar */}
        {showSave && (
          <div style={{ display:"flex", gap:12, marginTop:28, paddingTop:20, borderTop:"1px solid rgba(0,53,128,0.09)" }}>
            <button onClick={handleSave} disabled={saving}
              style={{ ...S.yBtn(false), flex:1, padding:13, opacity:saving?0.7:1, cursor:saving?"not-allowed":"pointer" }}>
              <FaSave size={14}/> {saving ? "Saving…" : "Save Configuration"}
            </button>
            <button onClick={handleReset} disabled={!dirty}
              style={{ ...S.bBtn(false), flex:1, padding:13, opacity:dirty?1:0.4, cursor:dirty?"pointer":"not-allowed" }}>
              <FaUndo size={13}/> Reset to Last Saved
            </button>
          </div>
        )}
      </div>
      </div>{/* end centered wrapper */}
    </div>
  );
}
